import React, { useCallback, useState } from "react";
import { Editor } from "@tiptap/core";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { useDispatch } from "react-redux";
import { NotificationType } from "@markit/common.types";

interface ImageSizeControlsProps {
  editor: Editor;
  selectedImage: ImageControl | null;
  setSelectedImage: (image: ImageControl | null) => void;
}

interface SizeOption {
  key: string;
  label: string;
}

export interface ImageControl {
  element: HTMLImageElement;
  currentSize: string;
  id: string;
}

interface ControlBarProps {
  editor: Editor;
  imageControl: ImageControl;
  onSizeChange: (imageId: string, newSize: string) => void;
  onClose: (imageId: string) => void;
}

const ControlBar: React.FC<ControlBarProps> = ({
  editor,
  imageControl,
  onSizeChange,
  onClose,
}) => {
  const { element: imageElement, currentSize, id } = imageControl;
  const rect = imageElement.getBoundingClientRect();
  const editorRect = editor.view.dom.getBoundingClientRect();

  const sizeOptions: SizeOption[] = [
    { key: "narrow", label: "Narrow" },
    { key: "medium", label: "Medium" },
    // { key: "wide", label: "Wide" }, // TODO
  ];

  const styles = {
    container: {
      position: "absolute" as const,
      bottom: editorRect.bottom - rect.bottom + 8, // Relative to editor
      left: 0,
      right: 0,
      marginInline: "auto",
      width: "fit-content",
      backgroundColor: Colors.GRAY6,
      borderRadius: 6,
      padding: 4,
      zIndex: 99,
      pointerEvents: "auto" as const,
      gap: 7,
    },
    button: (isActive: boolean) => ({
      flex: 1,
      paddingInline: 14,
      paddingBlock: 10,
      borderRadius: 8,
      background: isActive ? Colors.WHITE : Colors.TRANSPARENT,
      color: Colors.BLACK,
      cursor: "pointer",
      transition: "all 0.2s ease",
    }),
    closeButton: {
      position: "absolute" as const,
      top: rect.top - editorRect.top + 8,
      right: editorRect.right - rect.right + 8,
      width: 35,
      height: 35,
      borderRadius: 10,
      backgroundColor: Colors.WHITE1,
      border: `0.5px solid ${Colors.GRAY2}`,
      cursor: "pointer",
      zIndex: 99,
    },
  };

  return (
    <div>
      <div className="AlignedRowSpaced" style={styles.container}>
        {sizeOptions.map(({ key, label }) => (
          <div
            key={key}
            style={styles.button(key === currentSize)}
            onClick={() => onSizeChange(id, label)}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <span className="bodyMedium">{label}</span>
          </div>
        ))}
      </div>
      <div
        className="Centering"
        style={styles.closeButton}
        onClick={() => onClose(id)}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        <Icon icon="ion:close" height={20} color={Colors.BLACK} />
      </div>
    </div>
  );
};

const ImageSizeControls: React.FC<ImageSizeControlsProps> = (props) => {
  const { editor, selectedImage, setSelectedImage } = props;
  const dispatch = useDispatch();
  const [confirmRemoveModal, setConfirmRemoveModal] = useState(false);

  const updateImageSize = useCallback(
    (imageId: string, newSize: string) => {
      if (!selectedImage || selectedImage.id !== imageId) return;

      const { element: imageElement } = selectedImage;
      const containerWidth = editor.view.dom.offsetWidth;

      let newCurrentSize = "medium";
      let newWidth: number | string = containerWidth;

      if (newSize === "Narrow") {
        // Narrow: 75% of container width, centered
        newWidth = containerWidth * 0.75;
        newCurrentSize = "narrow";
      } else if (newSize === "Medium") {
        // Medium: 100% of container width
        newWidth = containerWidth;
        newCurrentSize = "medium";
      } else if (newSize === "Wide") {
        // Wide: Full screen viewport width using vw units
        newWidth = "100vw";
        newCurrentSize = "wide";
      }

      // Find the image node in the editor document and update its attributes
      const { state } = editor;
      const { doc } = state;

      let imagePos = -1;
      doc.descendants((node, pos) => {
        if (node.type.name === "image" && node.attrs.src === imageElement.src) {
          const domNode = editor.view.nodeDOM(pos) as HTMLElement;
          if (domNode && domNode.contains(imageElement)) {
            imagePos = pos;
            return false; // Stop searching
          }
        }
      });

      if (imagePos !== -1) {
        // Update the image node attributes in the editor
        editor
          .chain()
          .focus()
          .setNodeSelection(imagePos)
          .updateAttributes("image", {
            // fullWidth: newCurrentSize === "wide",
            width: newWidth,
            size: newCurrentSize,
          })
          .run();

        // Check if the image element is still valid after update
        setTimeout(() => {
          const updatedImageElement = editor.view.dom.querySelector(
            `img[src="${imageElement.src}"]`
          );

          // If the element is different, update our reference
          if (updatedImageElement && updatedImageElement !== imageElement) {
            setSelectedImage({
              ...selectedImage,
              element: updatedImageElement as HTMLImageElement,
              currentSize: newCurrentSize,
            });
          }
        }, 0);
      }

      // Update the image control with new size
      setSelectedImage({ ...selectedImage, currentSize: newCurrentSize });
    },
    [editor, selectedImage, setSelectedImage]
  );

  const handleClose = useCallback(
    (imageId: string) => {
      if (!selectedImage || selectedImage.id !== imageId) return;
      const { element: imageElement } = selectedImage;

      // Find the image node in the editor document
      const { state } = editor;
      const { doc } = state;

      // Find the position of the image in the document
      let imagePos = -1;
      doc.descendants((node, pos) => {
        if (node.type.name === "image" && node.attrs.src === imageElement.src) {
          // Additional check to ensure we're removing the correct image
          // by comparing position and other attributes
          const domNode = editor.view.nodeDOM(pos) as HTMLElement;
          if (domNode && domNode.contains(imageElement)) {
            imagePos = pos;
            return false; // Stop searching
          }
        }
      });

      if (imagePos !== -1) {
        // Remove the image from the editor
        editor
          .chain()
          .focus()
          .deleteRange({ from: imagePos, to: imagePos + 1 })
          .run();
      }

      // Remove from selected image
      setSelectedImage(null);
      showNotificationBanner(
        dispatch,
        "Image Removed",
        NotificationType.NEGATIVE
      );
    },
    [editor, selectedImage, setSelectedImage, dispatch]
  );

  // (TODO) Handle window resize for wide images
  // useEffect(() => {
  //   const handleResize = () => {
  //     // Update wide image when screen size changes
  //     if (selectedImage && selectedImage.currentSize === "wide") {
  //       const { element: imageElement } = selectedImage;
  //       // For vw units, no manual recalculation needed - browser handles it automatically
  //       // Just ensure the positioning is correct
  //       imageElement.style.setProperty("width", "100vw", "important");
  //       imageElement.style.setProperty("max-width", "100vw", "important");
  //       imageElement.style.left = "50%";
  //       imageElement.style.transform = "translateX(-50%)";
  //     }
  //   };

  //   window.addEventListener("resize", handleResize);
  //   return () => window.removeEventListener("resize", handleResize);
  // }, [selectedImage, editor]);

  if (!selectedImage) return null;

  return (
    <>
      <ControlBar
        editor={editor}
        key={selectedImage.id}
        imageControl={selectedImage}
        onSizeChange={updateImageSize}
        onClose={() => setConfirmRemoveModal(true)}
      />
      <ConfirmDeleteModal
        heading={"Remove Image?"}
        subtext="This image will be removed."
        deleteButtonText="Remove"
        hideModal={!confirmRemoveModal}
        setIsVisible={setConfirmRemoveModal}
        deleteOnPress={() => handleClose(selectedImage.id)}
      />
    </>
  );
};

export default ImageSizeControls;
